import { useInfiniteQuery } from '@tanstack/react-query'
import {
  getUserVotes,
  type UserVotesOrderBy,
  type UserVotesOrderDirection,
} from '../api/userApi'
import { useAppContext } from '../contexts/App.context'
import { DEFAULT_PAGINATION } from '../api/boxApi'
import { useEffect } from 'react'
import { getRefetchInterval } from '../utils/polling'

export interface UseUserVotesParams {
  orderBy?: UserVotesOrderBy
  orderDirection?: UserVotesOrderDirection
  limit?: number
  enabled?: boolean
}

export const useUserVotes = (params: UseUserVotesParams = {}) => {
  const { canFetchUserData } = useAppContext()
  const {
    orderBy,
    orderDirection,
    limit = DEFAULT_PAGINATION.limit,
    enabled = true,
  } = params

  const query = useInfiniteQuery({
    queryKey: ['userVotes', orderBy, orderDirection, limit],
    queryFn: async ({ pageParam = 1 }) =>
      await getUserVotes({ orderBy, orderDirection, page: pageParam, limit }),
    getNextPageParam: lastPage => {
      if (lastPage.meta.totalPages <= lastPage.meta.page) {
        return undefined
      }
      return Number(lastPage.meta.page) + 1
    },
    initialPageParam: 1,
    enabled: canFetchUserData && enabled,
    refetchInterval: getRefetchInterval(),
  })

  useEffect(() => {
    if (enabled && canFetchUserData && !query.isFetching && !query.data) {
      query.refetch()
    }
  }, [canFetchUserData, enabled, query])

  return query
}
